import styled from '@emotion/styled';
import type { FunctionComponent } from 'react';
import { Fragment, memo, useCallback } from 'react';
import type { Control } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { PulsatingCircle } from '~/ui/core/icons';
import { RichEditor } from '~/ui/core/rich-editor/RichEditor';

import { SectionContent } from '../components/Common';
import { RichEditorToolbarWithTranscribe } from './RichEditorToolbarWithTranscribe';
import type { ProfileForm } from './types';

export type EditableDescriptionSectionProps = {
    readonly activeLanguageIndex: number;
    readonly control: Control<ProfileForm>;
    readonly index: number;
    readonly isGenerating: boolean;
    readonly onUserInput?: () => void;
    readonly sectionTitle: string;
};

const EditorContainer = styled.div`
    &:focus,
    &:focus-visible {
        border: none;
        box-shadow: none;
        outline: none;
    }

    position: relative;
`;

const TitleText = styled.div`
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.black[900]};
    font-family: ${({ theme }) => theme.typography.body.S.medium.fontFamily};
    font-size: ${({ theme }) => theme.typography.body.S.medium.fontSize};
    font-style: normal;
    font-weight: ${({ theme }) => theme.typography.body.S.medium.fontWeight};
    line-height: ${({ theme }) => theme.typography.body.S.medium.lineHeight};
    outline: none;
    text-transform: capitalize;
    margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const TitleWithLoaderContainer = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing.s};
    justify-content: flex-start;
    margin-bottom: ${({ theme }) => theme.spacing.m};
`;

const EditorWrapper = styled.div(({ theme }) => ({
    border: `1px solid ${theme.colors.gray[400]}`,
    borderRadius: theme.spacing.s,
    overflow: 'hidden',
}));

const EditableDescriptionSectionComponent: FunctionComponent<EditableDescriptionSectionProps> = ({
    activeLanguageIndex,
    control,
    index,
    isGenerating,
    onUserInput,
    sectionTitle,
}) => {
    const {
        field: { onChange, value },
    } = useController({ control, name: `translations.${activeLanguageIndex}.structured.${index}.value` });

    const handleEditorChange = useCallback(
        (html: string) => {
            onChange(html);
        },
        [onChange],
    );

    return (
        <EditorContainer data-testid="editable-description-section-container">
            <TitleWithLoaderContainer>
                <TitleText>{sectionTitle}</TitleText>
                {isGenerating && <PulsatingCircle />}
            </TitleWithLoaderContainer>
            <EditorWrapper>
                <RichEditor
                    toolbar={<RichEditorToolbarWithTranscribe />}
                    value={value}
                    onChange={handleEditorChange}
                    onUserInput={onUserInput}
                />
            </EditorWrapper>
        </EditorContainer>
    );
};

export const EditableDescriptionSection = memo(EditableDescriptionSectionComponent);
EditableDescriptionSection.displayName = 'EditableDescriptionSection';

export const DescriptionSection: FunctionComponent<{ readonly title: string; readonly value: string }> = ({
    title,
    value,
}) => {
    return (
        <Fragment>
            <TitleText>{title}</TitleText>
            {/** biome-ignore lint/security/noDangerouslySetInnerHtml: suppressed due to migration */}
            <SectionContent dangerouslySetInnerHTML={{ __html: value }} data-testid="description-section-content" />
        </Fragment>
    );
};
